import React, { useState, useEffect, useRef } from "react";
import { CiSearch } from "react-icons/ci";
import Button from 'react-bootstrap/Button';
import AddPatientFilters from "./AddPatientFilters";

function DefaultPatientFilters({ defaultFilters = [], additionalFilters = [], selectedFilters, setSelectedFilters,setHasData }) {
  const [showDropdown, setShowDropdown] = useState(null);
  const [filters, setFilters] = useState({});
  const dropdownRefs = useRef({});

  const toggleDropdown = (key) => {
    setShowDropdown(prev => (prev === key ? null : key));
  };
   
  const toggleFilterOption = (key, value) => {
    setFilters(prev => {
      const currentSet = new Set(prev[key]?.values || []);
      currentSet.has(value) ? currentSet.delete(value) : currentSet.add(value);

      if (currentSet.size === 0) {
        const { [key]: _, ...rest } = prev;
        return rest;
      }
      return {
        ...prev,
        [key]: { values: currentSet, operator: "Equals" }
      };
    });
  };

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        showDropdown &&
        dropdownRefs.current[showDropdown] &&
        !dropdownRefs.current[showDropdown].contains(event.target)
      ) {
        setShowDropdown(null);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [showDropdown]);

  const handleApply = () => {
    setSelectedFilters(prev => {
      const updated = { ...prev, ...filters };
      Object.keys(prev).forEach(key => {
        if (!(key in filters)) {
          delete updated[key];
        }
      });
      return updated;
    });
    setShowDropdown(null);
  };

  const handleReset = (key) => {
    setFilters(prev => {
      const { [key]: _, ...rest } = prev;
      return rest;
    });
    setSelectedFilters(prev => {
      const { [key]: _, ...rest } = prev;
      return rest;
    });
    setHasData(true);
  };

  const filterStyle = `
    /* Filter Button - Clean Gray Design */
    .filter-btn {
      background: #f8f9fa;
      color: #495057;
      font-size: 13px;
      font-weight: 500;
      padding: 8px 16px;
      border: 1px solid #e9ecef;
      border-radius: 8px;
      display: flex;
      align-items: center;
      gap: 8px;
      transition: all 0.25s cubic-bezier(0.4, 0, 0.2, 1);
      cursor: pointer;
      position: relative;
      overflow: hidden;
    }

    /* Hover effect with left-to-right gradient sweep */
    .filter-btn::before {
      content: '';
      position: absolute;
      top: 0;
      left: -100%;
      width: 100%;
      height: 100%;
      background: linear-gradient(90deg, transparent, rgba(108, 117, 125, 0.1), transparent);
      transition: left 0.5s ease;
    }

    .filter-btn:hover::before {
      left: 100%;
    }

    .filter-btn:hover {
      background: #e9ecef;
      border-color: #ced4da;
      color: #343a40;
      transform: translateY(-1px);
      box-shadow: 0 2px 8px rgba(0, 0, 0, 0.08);
    }

    .filter-btn.active {
      background: #6c757d;
      color: white;
      border-color: #6c757d;
      box-shadow: 0 2px 8px rgba(108, 117, 125, 0.25);
    }

    .filter-btn.active::before {
      display: none;
    }

    /* Selection count badge */
    .selection-count {
      background: #dc3545;
      color: white;
      font-size: 11px;
      font-weight: 600;
      min-width: 18px;
      height: 18px;
      border-radius: 9px;
      display: flex;
      align-items: center;
      justify-content: center;
      margin-left: 4px;
      animation: countBounce 0.3s ease-out;
    }

    @keyframes countBounce {
      0% { transform: scale(0.3); opacity: 0; }
      50% { transform: scale(1.1); }
      100% { transform: scale(1); opacity: 1; }
    }

    .filter-btn.has-selection {
      background: #f1f3f4;
      border-color: #adb5bd;
      color: #495057;
    }

    .filter-btn.has-selection:hover {
      background: #e9ecef;
      border-color: #6c757d;
    }

    .filter-btn.has-selection.active {
      background: #6c757d;
      color: white;
      border-color: #6c757d;
    }

    .filter-btn.has-selection.active .selection-count {
      background: rgba(255, 255, 255, 0.9);
      color: #6c757d;
    }

    /* Dropdown Animation */
    .dropdown-container {
      position: absolute;
      right:0px;
      z-index: 30;
      margin-top: 8px;
      width: 280px;
      background: white;
      border: 1px solid #e9ecef;
      border-radius: 12px;
      box-shadow: 0 10px 25px rgba(0, 0, 0, 0.1);
      animation: dropdownSlide 0.2s ease-out forwards;
      transform-origin: top;
    }

    @keyframes dropdownSlide {
      0% { 
        opacity: 0; 
        transform: translateY(-10px) scale(0.95); 
      }
      100% { 
        opacity: 1; 
        transform: translateY(0) scale(1); 
      }
    }

    /* Dropdown Header */
    .dropdown-header {
      padding: 16px;
      border-bottom: 1px solid #f1f3f4;
      background: #fafbfc;
      border-radius: 12px 12px 0 0;
    }

    .dropdown-title {
      font-size: 14px;
      font-weight: 600;
      color: #495057;
      margin: 0;
    }

    /* Options Container */
    .options-container {
      padding: 12px 0;
      max-height: 240px;
      overflow-y: auto;
    }

    /* Custom Checkbox Design */
    .checkbox-item {
      display: flex;
      align-items: center;
      padding: 8px 16px;
      cursor: pointer;
      transition: all 0.2s ease;
      position: relative;
      overflow: hidden;
    }

    .checkbox-item::before {
      content: '';
      position: absolute;
      top: 0;
      left: -100%;
      width: 100%;
      height: 100%;
      background: linear-gradient(90deg, transparent, rgba(108, 117, 125, 0.05), transparent);
      transition: left 0.4s ease;
    }

    .checkbox-item:hover::before {
      left: 100%;
    }

    .checkbox-item:hover {
      background-color: #f8f9fa;
    }

    .checkbox-item.selected {
      background-color: #f1f3f4;
      border-left: 3px solid #6c757d;
    }

    .checkbox-item.selected .checkbox-label {
      color: #495057;
      font-weight: 500;
    }

    .custom-checkbox {
      position: relative;
      width: 18px;
      height: 18px;
      margin-right: 12px;
      flex-shrink: 0;
    }

    .custom-checkbox input {
      position: absolute;
      opacity: 0;
      cursor: pointer;
      width: 100%;
      height: 100%;
    }

    .checkmark {
      position: absolute;
      top: 0;
      left: 0;
      height: 18px;
      width: 18px;
      background-color: #fff;
      border: 2px solid #dee2e6;
      border-radius: 4px;
      transition: all 0.2s ease;
    }

    .custom-checkbox:hover .checkmark {
      border-color: #6c757d;
      background-color: #f8f9fa;
    }

    .custom-checkbox input:checked ~ .checkmark {
      background-color: #6c757d;
      border-color: #6c757d;
    }

    .checkmark:after {
      content: "";
      position: absolute;
      display: none;
      left: 5px;
      top: 2px;
      width: 4px;
      height: 8px;
      border: solid white;
      border-width: 0 2px 2px 0;
      transform: rotate(45deg);
    }

    .custom-checkbox input:checked ~ .checkmark:after {
      display: block;
    }

    .checkbox-label {
      font-size: 14px;
      color: #495057;
      line-height: 1.4;
      flex: 1;
    }

    /* Action Buttons */
    .actions-container {
      padding: 12px 16px;
      border-top: 1px solid #f1f3f4;
      background: #fafbfc;
      border-radius: 0 0 12px 12px;
      display: flex;
      gap: 8px;
      justify-content: flex-end;
    }

    .apply-btn {
      background: #6c757d;
      color: white;
      border: none;
      font-size: 13px;
      font-weight: 500;
      border-radius: 6px;
      padding: 8px 16px;
      transition: all 0.2s ease;
      cursor: pointer;
    }

    .apply-btn:hover {
      background: #5a6268;
      transform: translateY(-1px);
      box-shadow: 0 2px 6px rgba(108, 117, 125, 0.25);
    }

    .reset-btn {
      background: transparent;
      color: #6c757d;
      border: 1px solid #dee2e6;
      font-size: 13px;
      font-weight: 500;
      border-radius: 6px;
      padding: 8px 16px;
      transition: all 0.2s ease;
      cursor: pointer;
    }

    .reset-btn:hover {
      background: #f8f9fa;
      border-color: #6c757d;
    }

    /* Scrollbar Styling */
    .options-container::-webkit-scrollbar {
      width: 4px;
    }

    .options-container::-webkit-scrollbar-track {
      background: #f1f3f4;
      border-radius: 2px;
    }

    .options-container::-webkit-scrollbar-thumb {
      background: #dee2e6;
      border-radius: 2px;
    }

    .options-container::-webkit-scrollbar-thumb:hover {
      background: #ced4da;
    }
  `;

  return (
    <>
      <style>{filterStyle}</style>
      <div className="flex items-center gap-3">
        {defaultFilters.map(({ label, key, options, inputType }) => (
  <div
    key={key}
    className="relative inline-block text-left"
    ref={(el) => (dropdownRefs.current[key] = el)}
  >
    <button
      onClick={() => toggleDropdown(key)}
      className={`filter-btn ${showDropdown === key ? "active" : ""} ${filters[key]?.values?.size > 0 ? "has-selection" : ""}`}
    >
      <CiSearch size={16} />
      {label}
      {filters[key]?.values?.size > 0 && (
        <span className="selection-count">
          {filters[key].values.size}
        </span>
      )}
    </button>

    {showDropdown === key && (
      <div className="dropdown-container">
        <div className="dropdown-header">
          <h3 className="dropdown-title">Filter by {label}</h3>
        </div>

        <div className="options-container">
          {inputType === "date-range" ? (
            <div className="flex flex-col gap-2 p-2">
              <label className="text-sm font-semibold">From</label>
              <input
                type="date"
                className="border px-2 py-1 rounded-md text-sm"
                onChange={(e) =>
                  setFilters(prev => ({
                    ...prev,
                    [key]: {
                      ...prev[key],
                      from: e.target.value,
                      operator: "Between"
                    }
                  }))
                }
              />
              <label className="text-sm font-semibold">To</label>
              <input
                type="date"
                className="border px-2 py-1 rounded-md text-sm"
                onChange={(e) =>
                  setFilters(prev => ({
                    ...prev,
                    [key]: {
                      ...prev[key],
                      to: e.target.value,
                      operator: "Between"
                    }
                  }))
                }
              />
            </div>
          ) : (
            options.map((option) => (
              <div
                key={option}
                className={`checkbox-item ${filters[key]?.values?.has(option) ? 'selected' : ''}`}
                onClick={() => toggleFilterOption(key, option)}
              >
                <label className="custom-checkbox">
                  <input
                    type="checkbox"
                    checked={filters[key]?.values ? filters[key]?.values?.has(option) : false}
                    onClick={() => toggleFilterOption(key, option)}
                  />
                  <span className="checkmark"></span>
                </label>  
                <span className="checkbox-label">{option}</span>
              </div>
            ))
          )}
        </div>

        <div className="actions-container">
          <button
            className="reset-btn"
            onClick={() => handleReset(key)}
          >
            Reset
          </button>
          <button
            className="apply-btn"
            onClick={handleApply}
          >
            Apply
          </button>
        </div>
      </div>
    )}
  </div>
))}


        {additionalFilters.length > 0 && (
          <AddPatientFilters
            filters={additionalFilters} 
            setSelectedFilters={setSelectedFilters}
            selectedFilters={selectedFilters}
          />
        )}
      </div>
    </>
  );
}

export default DefaultPatientFilters;